import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { RegisterFormData } from '../types';
import { GoogleLoginButton } from '../components/ui/GoogleLoginButton';
import { Card } from '../components/ui/Card';
import { Button } from '../components/ui/Button';
import { FormField } from '../components/ui/FormField';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { ThemeToggleButton } from '../components/ui/ThemeToggleButton';
import { ThemeAwareLogo } from '../components/ui/ThemeAwareLogo';
import { referralService } from '../services/referralService';

export const RegisterPage: React.FC = () => {
  const navigate = useNavigate();
  const { register, isLoading, error, clearError } = useAuth();
  const [formData, setFormData] = useState<RegisterFormData>({
    email: '',
    password: '',
    confirmPassword: '',
    firstName: '',
    lastName: '',
    referralCode: ''
  });
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
  const [referralCodeValid, setReferralCodeValid] = useState<boolean | null>(null);
  const [referralCodeLoading, setReferralCodeLoading] = useState(false);
  const [referralCodeMessage, setReferralCodeMessage] = useState('');

  // Check for referral code in URL on component mount
  useEffect(() => {
    const referralCode = referralService.getReferralCodeFromUrl();
    if (referralCode) {
      setFormData(prev => ({ ...prev, referralCode }));
      validateReferralCode(referralCode);
    }
  }, []);

  const validateReferralCode = async (code: string) => {
    if (!code.trim()) {
      setReferralCodeValid(null);
      setReferralCodeMessage('');
      return;
    }

    setReferralCodeLoading(true);
    try {
      const result = await referralService.validateReferralCode(code);
      setReferralCodeValid(result.valid);
      setReferralCodeMessage(result.valid 
        ? `Valid referral code from ${result.referrer_email}` 
        : result.error || 'Invalid referral code'
      );
    } catch (error) {
      setReferralCodeValid(false);
      setReferralCodeMessage('Error validating referral code');
    } finally {
      setReferralCodeLoading(false);
    }
  };

  const validateForm = (): boolean => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      errors.email = 'Please enter a valid email address';
    }

    if (!formData.password) {
      errors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      errors.password = 'Password must be at least 8 characters';
    } else if (!/(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/.test(formData.password)) {
      errors.password = 'Password must contain at least one uppercase letter, one lowercase letter, and one number';
    }

    if (formData.password !== formData.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    clearError();
    
    try {
      await register(formData);
      navigate('/app/dashboard');
    } catch (err) {
      console.error('Registration error:', err);
    }
  };

  const handleInputChange = (field: keyof RegisterFormData, value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Clear auth error when user starts typing
    if (error) {
      clearError();
    }

    // Validate referral code when it changes
    if (field === 'referralCode') {
      // Debounce validation
      const timeoutId = setTimeout(() => {
        validateReferralCode(value);
      }, 500);
      
      return () => clearTimeout(timeoutId);
    }
  };

  const handleGoogleError = (error: string) => {
    console.error('Google signup error:', error);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <LoadingSpinner size="lg" text="Creating your account..." />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col justify-center py-10 px-4 sm:px-6 lg:px-8 page-gradient-bg relative">
      {/* Top Bar Navigation */}
      <div className="fixed top-4 left-4 z-50">
        <Link
          to="/"
          className="inline-flex items-center text-xs sm:text-sm font-semibold text-gray-600 dark:text-gray-300 hover:text-indigo-600 dark:hover:text-indigo-400 bg-white/80 dark:bg-gray-800/80 backdrop-blur-md px-3 py-1.5 rounded-lg border border-gray-200/80 dark:border-gray-700 shadow-sm transition-all"
        >
          ← Back to Home
        </Link>
      </div>
      <div className="fixed top-4 right-4 z-50">
        <ThemeToggleButton />
      </div>

      <div className="max-w-md w-full mx-auto">
        <Card className="shadow-2xl border border-gray-200/80 dark:border-gray-700/80 p-6 sm:p-8 rounded-2xl glass-card">
          <div className="text-center mb-8">
            <div className="flex justify-center mb-4">
              <ThemeAwareLogo size="xl" showText={false} />
            </div>
            <h2 className="text-2xl sm:text-3xl font-extrabold text-gray-900 dark:text-white tracking-tight">
              Create Your Account
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
              Join IntFinder and start investigating in seconds
            </p>
          </div>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            {error && (
              <div className="bg-rose-50 dark:bg-rose-950/40 border border-rose-200 dark:border-rose-800/60 rounded-xl p-3.5 text-rose-700 dark:text-rose-300 text-sm flex items-center gap-3">
                <svg className="w-5 h-5 flex-shrink-0 text-rose-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span>{error}</span>
              </div>
            )}
            
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <FormField
                label="First Name"
                htmlFor="firstName"
                required
                error={validationErrors.firstName}
              >
                <input
                  id="firstName"
                  name="firstName"
                  type="text"
                  autoComplete="given-name"
                  required
                  value={formData.firstName}
                  onChange={(e) => handleInputChange('firstName', e.target.value)}
                  className="input-field"
                  placeholder="Enter your first name"
                  disabled={isLoading}
                />
              </FormField>

              <FormField
                label="Last Name"
                htmlFor="lastName"
                required
                error={validationErrors.lastName}
              >
                <input
                  id="lastName"
                  name="lastName"
                  type="text"
                  autoComplete="family-name"
                  required
                  value={formData.lastName}
                  onChange={(e) => handleInputChange('lastName', e.target.value)}
                  className="input-field"
                  placeholder="Enter your last name"
                  disabled={isLoading}
                />
              </FormField>
            </div>

            <FormField
              label="Email Address"
              htmlFor="email"
              required
              error={validationErrors.email}
            >
              <input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                required
                value={formData.email}
                onChange={(e) => handleInputChange('email', e.target.value)}
                className="input-field"
                placeholder="Enter your email address"
                disabled={isLoading}
              />
            </FormField>

            <FormField
              label="Password"
              htmlFor="password"
              required
              error={validationErrors.password}
              helpText="Must be at least 8 characters with uppercase, lowercase, and number"
            >
              <input
                id="password"
                name="password"
                type="password"
                autoComplete="new-password"
                required
                value={formData.password}
                onChange={(e) => handleInputChange('password', e.target.value)}
                className="input-field"
                placeholder="Create a strong password"
                disabled={isLoading}
              />
            </FormField>

            <FormField
              label="Confirm Password"
              htmlFor="confirmPassword"
              required
              error={validationErrors.confirmPassword}
            >
              <input
                id="confirmPassword"
                name="confirmPassword"
                type="password"
                autoComplete="new-password"
                required
                value={formData.confirmPassword}
                onChange={(e) => handleInputChange('confirmPassword', e.target.value)}
                className="input-field"
                placeholder="Confirm your password"
                disabled={isLoading}
              />
            </FormField>

            <FormField
              label="Referral Code (Optional)"
              htmlFor="referralCode"
              helpText="Enter a referral code to get 20 Int Coins signup bonus"
            >
              <div className="relative">
                <input
                  id="referralCode"
                  name="referralCode"
                  type="text"
                  value={formData.referralCode}
                  onChange={(e) => handleInputChange('referralCode', e.target.value)}
                  className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 transition-colors ${
                    referralCodeValid === true 
                      ? 'border-green-300 bg-green-50 dark:bg-green-900/20' 
                      : referralCodeValid === false 
                        ? 'border-red-300 bg-red-50 dark:bg-red-900/20' 
                        : 'border-gray-300 dark:border-gray-600'
                  }`}
                  placeholder="Enter referral code"
                  disabled={isLoading}
                />
                {referralCodeLoading && (
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-indigo-600"></div>
                  </div>
                )}
              </div>
              {referralCodeMessage && (
                <p className={`mt-1 text-sm ${
                  referralCodeValid === true 
                    ? 'text-green-600 dark:text-green-400' 
                    : referralCodeValid === false 
                      ? 'text-red-600 dark:text-red-400' 
                      : 'text-gray-600 dark:text-gray-300'
                }`}>
                  {referralCodeMessage}
                </p>
              )}
            </FormField>

            <Button
              type="submit"
              variant="primary"
              className="w-full py-3 rounded-xl font-semibold shadow-lg shadow-indigo-600/25"
              loading={isLoading}
            >
              {isLoading ? 'Creating account...' : 'Create Free Account ⚡'}
            </Button>
          </form>

          {/* Divider */}
          <div className="my-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-200 dark:border-gray-700" />
              </div>
              <div className="relative flex justify-center text-xs uppercase tracking-wider">
                <span className="px-3 bg-white dark:bg-gray-800 text-gray-500 dark:text-gray-400">Or continue with</span>
              </div>
            </div>
          </div>

          {/* Google Login Button */}
          <div className="mb-6 flex justify-center">
            <GoogleLoginButton
              onSuccess={() => {}}
              onError={handleGoogleError}
              text="Continue with Google"
              disabled={isLoading}
            />
          </div>

          {/* Sign In Link */}
          <div className="text-center pt-2">
            <p className="text-sm text-gray-600 dark:text-gray-300">
              Already have an account?{' '}
              <Link
                to="/login"
                className="font-semibold text-indigo-600 dark:text-indigo-400 hover:underline transition-colors"
              >
                Sign in here
              </Link>
            </p>
          </div>
        </Card>
      </div>
    </div>
  );
}; 